import { Component, input, output } from '@angular/core';

import { UserSessionDTO } from '../../../types/user-profile.types';
import { SessionInvalidateButtonComponent } from './session-invalidate-button.component';

@Component({
  standalone: true,
  imports: [SessionInvalidateButtonComponent],
  selector: 'sb-session-item',
  template: `
    <div
      class="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-gray-300 transition-colors duration-200"
    >
      <!-- Mobile: Stack vertically, Desktop: Side by side -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-start sm:items-center space-x-3">
            <!-- Device Icon -->
            <div class="flex-shrink-0">
              <div
                class="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-lg flex items-center justify-center"
              >
                <svg
                  class="w-4 h-4 sm:w-5 sm:h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  @if (isMobileDevice()) {
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"
                  />
                  } @else {
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                  }
                </svg>
              </div>
            </div>

            <!-- Session Info -->
            <div class="flex-1 min-w-0">
              <div
                class="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2"
              >
                <p
                  class="text-sm sm:text-base font-medium text-gray-900 truncate"
                >
                  {{ getDeviceInfo() }}
                </p>
                @if (session().is_current_session) {
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 self-start"
                >
                  Current
                </span>
                }
              </div>

              <!-- Location and IP info - Stack on mobile -->
              <div
                class="mt-1 flex flex-col sm:flex-row sm:items-center space-y-0.5 sm:space-y-0 sm:space-x-1 text-xs text-gray-500"
              >
                <div class="flex items-center space-x-1">
                  <svg
                    class="w-3 h-3 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="truncate">{{ session().ip_address }}</span>
                </div>
                <span class="hidden sm:inline">•</span>
                <span class="truncate">{{ getLocationInfo() }}</span>
              </div>

              <div class="mt-1 text-xs text-gray-500">
                <span class="sm:hidden">Active: </span
                >{{ formatDate(session().last_activity) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Actions - Full width button on mobile -->
        <sb-session-invalidate-button
          [sessionId]="session().session_id"
          [isCurrentSession]="session().is_current_session"
          (invalidateSession)="onInvalidateSession($event)"
        />
      </div>
    </div>
  `,
})
export class SessionItemComponent {
  session = input.required<UserSessionDTO>();
  invalidateSession = output<string>();

  onInvalidateSession(sessionId: string) {
    this.invalidateSession.emit(sessionId);
  }

  isMobileDevice(): boolean {
    const userAgent = this.session().user_agent.toLowerCase();
    return (
      userAgent.includes('mobile') ||
      userAgent.includes('android') ||
      userAgent.includes('iphone')
    );
  }

  getDeviceInfo(): string {
    const userAgent = this.session().user_agent;

    // Extract browser info
    let browser = 'Unknown Browser';
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';

    // Extract OS info
    let os = 'Unknown OS';
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iOS')) os = 'iOS';

    const deviceType = this.isMobileDevice() ? 'Mobile' : 'Desktop';

    return `${browser} on ${os} (${deviceType})`;
  }

  getLocationInfo(): string {
    // This would typically come from a geolocation service
    // For now, just return a placeholder
    return 'Location unavailable';
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60)
      return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

    return date.toLocaleDateString();
  }
}
